<template>
  <div>
    <!-- Page Title Area -->
    <div class="page-title-area">
      <div class="container">
        <h2>{{ title }}</h2>
      </div>
    </div>

    <!-- Article Content Area -->
    <section class="ptb-80">
      <div class="container">
        <div v-if="loading" class="text-center">
          <div class="spinner-border" role="status">
            <span class="sr-only">Loading...</span>
          </div>
        </div>
        <div v-else-if="error" class="alert alert-danger" role="alert">
          {{ error }}
        </div>
        <div v-else class="blog-details-area">
          <div class="blog-details">
            <div class="article-img" v-if="article.Image">
              <img :src="article.Image" :alt="article.Title" class="img-fluid">
              <div class="date" v-if="article.Date">
                {{ formatDateShort(article.Date) }}
              </div>
            </div>
            <div class="article-content">
              <ul class="category" v-if="article.Category">
                <li><a href="javascript:void(0)">{{ article.Category }}</a></li>
              </ul>
              <div v-html="content"></div>

              <div class="share-post" v-if="showSocialSharing">
                <ul>
                  <li><a href="javascript:void(0)" @click="shareOnFacebook"><i data-feather="facebook"></i></a></li>
                  <li><a href="javascript:void(0)" @click="shareOnTwitter"><i data-feather="twitter"></i></a></li>
                  <li><a href="javascript:void(0)" @click="shareOnLinkedIn"><i data-feather="linkedin"></i></a></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { fetchCmsContent } from '../utils/api'
import feather from 'feather-icons'

export default {
  name: 'ArticleView',
  props: {
    slug: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const route = useRoute()
    const title = ref('')
    const content = ref('')
    const article = ref(null)
    const loading = ref(true)
    const error = ref(null)
    const showSocialSharing = ref(true)

    const formatDateShort = (dateString) => {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        month: 'short', 
        day: 'numeric'
      })
    }

    const shareOnFacebook = () => {
      const url = window.location.href
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank')
    }

    const shareOnTwitter = () => {
      const url = window.location.href
      const text = article.value?.Title || ''
      window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`, '_blank')
    }

    const shareOnLinkedIn = () => {
      const url = window.location.href
      const title = article.value?.Title || ''
      window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`, '_blank')
    }

    const fetchArticle = async (slug) => {
      loading.value = true
      error.value = null
      
      try {
        console.log(`Fetching article with slug: ${slug}`)
        const response = await fetchCmsContent('article', slug)
        
        if (!response) {
          error.value = 'Article not found'
          return
        }
        
        if (response.Title) {
          title.value = response.Title
          content.value = response.Content || ''
          article.value = response
          
          // Set meta information if available
          if (response.MetaTitle) {
            document.title = response.MetaTitle
          } else {
            document.title = `${response.Title} - AtOnline.com`
          }
          
          // Set meta description if available
          const metaDescription = document.querySelector('meta[name="description"]')
          if (metaDescription && response.MetaDescription) {
            metaDescription.setAttribute('content', response.MetaDescription)
          }
          
          // If we have dates, parse them properly
          if (article.value.Published && typeof article.value.Published === 'object') {
            article.value.Date = article.value.Published.iso
          } else if (article.value.Created && typeof article.value.Created === 'object') {
            article.value.Date = article.value.Created.iso
          }
        } else {
          console.warn('Article response missing Title:', response)
          error.value = 'Article content is invalid or incomplete'
        }
      } catch (err) {
        console.error('Error fetching article:', err)
        // Provide more specific error message based on error type
        if (err.message && err.message.includes('not found')) {
          error.value = 'Article not found'
        } else if (err.response && err.response.status === 404) {
          error.value = 'Article not found'
        } else if (err.response && err.response.status >= 500) {
          error.value = 'Server error when loading article. Please try again later.'
        } else {
          error.value = 'Error loading article content'
        }
      } finally {
        loading.value = false
        setTimeout(() => {
          feather.replace()
        }, 100)
      }
    }

    onMounted(() => {
      fetchArticle(props.slug)
    })

    // Watch for route changes
    watch(() => route.params.slug, (newSlug) => {
      if (newSlug && newSlug !== props.slug) {
        fetchArticle(newSlug)
      }
    })

    return {
      title,
      content,
      article,
      loading,
      error,
      formatDateShort,
      showSocialSharing,
      shareOnFacebook,
      shareOnTwitter,
      shareOnLinkedIn
    }
  }
}
</script>

<style scoped>
/* Using styles already defined in style.css */
</style>